import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import Logger from '@evidentid/logger';
import { GovernmentIssuedWcJurisdictions } from '@evidentid/tprm-portal-lib/models/common';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): { gatewayClient: GatewayApiClient, logger?: Logger } {
    return store.$app;
}

/** Waits before each retry of a failed load. One attempt plus one per entry. */
export const WC_JURISDICTIONS_RETRY_DELAYS_MS = [ 1000, 3000 ];

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

interface WcJurisdictionsState {
    jurisdictions: GovernmentIssuedWcJurisdictions | null;
    status: OperationStatus;
}

/**
 * eid-insurance's government-issued workers compensation jurisdictions — the list notification
 * routing also uses. Same for every RP and changes only with a deploy, so it is loaded once at boot.
 *
 * `jurisdictions` stays `null` until a load succeeds, and `null` means "not known yet", never "no
 * jurisdiction is government-issued": the producers card holds its edits until the list is here.
 *
 * A failure is reported to Sentry rather than shown: the list matters only to the producers card,
 * which says so itself, and a page-wide error would reach screens that never read it, the login
 * page among them. A later `load()` after a failure tries again.
 */
export const useWcJurisdictionsStore = defineStore('wcJurisdictions', {
    state: (): WcJurisdictionsState => ({
        jurisdictions: null,
        status: OperationStatus.uninitialized,
    }),
    getters: {
        loaded: (state): boolean => state.status === OperationStatus.success,
        failed: (state): boolean => state.status === OperationStatus.error,
    },
    actions: {
        async load(): Promise<void> {
            if (this.status === OperationStatus.loading || this.status === OperationStatus.success) {
                return;
            }
            this.status = OperationStatus.loading;
            for (let attempt = 0; ; attempt += 1) {
                try {
                    this.jurisdictions = await appOf(this).gatewayClient.tprmUi().getGovernmentIssuedWcJurisdictions();
                    this.status = OperationStatus.success;
                    return;
                } catch (error) {
                    if (attempt >= WC_JURISDICTIONS_RETRY_DELAYS_MS.length) {
                        this.status = OperationStatus.error;
                        appOf(this).logger?.error(error as Error);
                        return;
                    }
                    await wait(WC_JURISDICTIONS_RETRY_DELAYS_MS[attempt]);
                }
            }
        },
    },
});
